import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, Users, Award, Clock, Heart } from 'lucide-react';
import { PublicLayout, ScrollReveal, SectionTitle } from './components';
import { ABOUT, CLIENTS, SITE_CONFIG } from './config';
import { SEOHead } from './SEOHead';

const NosotrosPage = () => {
  return (
    <PublicLayout>
      <SEOHead page="nosotros" />
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/web" className="hover:text-emerald-600">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">Sobre nosotros</span>
            </nav>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {ABOUT.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              {ABOUT.subtitle}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Historia */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="prose prose-lg max-w-none">
              {ABOUT.story.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 lg:py-24 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionTitle 
              badge="Nuestros valores"
              title="Lo que nos define"
              light
            />
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {ABOUT.values.map((value, index) => {
              const icons = [Users, Award, Clock, Heart];
              const Icon = icons[index] || Check;
              
              return (
                <ScrollReveal key={value.title} delay={index * 100}>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-800 flex items-center justify-center">
                      <Icon className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                    <p className="text-emerald-200">{value.description}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clientes */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionTitle 
              badge="Nuestros clientes"
              title="Empresas que confían en nosotros"
              subtitle="Desde multinacionales hasta instituciones públicas, todos repiten."
            />
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
              {CLIENTS.map((client) => (
                <div 
                  key={client}
                  className="px-6 py-3 bg-gray-50 rounded-full text-gray-600 font-medium hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                >
                  {client}
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionTitle 
              badge="Nuestra trayectoria"
              title="Creciendo paso a paso"
            />
          </ScrollReveal>

          <div className="space-y-8">
            {[
              { year: '2018', title: 'Nace Duetto Catering', description: 'Dos profesionales con experiencia deciden empezar un proyecto desde cero.' },
              { year: '2019', title: 'Primeros clientes recurrentes', description: 'La calidad del servicio empieza a generar confianza y repeticiones.' },
              { year: '2020', title: 'Adaptación y resiliencia', description: 'Nos reinventamos para seguir sirviendo a empresas en tiempos difíciles.' },
              { year: '2022', title: 'Crecimiento sostenido', description: 'Incorporamos grandes cuentas como eBay, TikTok y Repsol.' },
              { year: '2024', title: 'Consolidación', description: '+500 eventos realizados y miles de comensales satisfechos.' },
            ].map((item, index) => (
              <ScrollReveal key={item.year} delay={index * 100}>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-20 text-right">
                    <span className="text-2xl font-bold text-emerald-600">{item.year}</span>
                  </div>
                  <div className="flex-shrink-0 w-px bg-emerald-200 relative">
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-emerald-500" />
                  </div>
                  <div className="pb-8">
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 mt-1">{item.description}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Quieres conocernos mejor?
            </h2>
            <p className="text-lg text-emerald-100 mb-8">
              Cuéntanos tu próximo evento y descubre por qué tantas empresas confían en nosotros.
            </p>
            <Link
              to="/web/contacto"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-700 font-semibold rounded-full hover:bg-emerald-50 transition-colors"
            >
              Contactar con nosotros
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NosotrosPage;
